import { useState, useEffect, useCallback } from 'react';
import { notifications as notifApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Bell, CheckCircle, XCircle, CreditCard, ShoppingBag, Hotel, Clock } from 'lucide-react';

const TYPE_ICONS = {
  booking_created: Hotel,
  booking_updated: Hotel,
  booking_cancelled: XCircle,
  booking_payment_confirmed: CreditCard,
  booking_transfer: ShoppingBag,
  booking_transferred: ShoppingBag,
};

const TYPE_COLORS = {
  booking_created: 'text-primary',
  booking_cancelled: 'text-red-500',
  booking_payment_confirmed: 'text-emerald-600',
  booking_transfer: 'text-purple-600',
  booking_transferred: 'text-purple-600',
};

export default function NotificationCenter({ hotelId }) {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    if (!hotelId) return;
    try {
      const { data } = await notifApi.list(hotelId);
      setNotifications(data);
      setUnreadCount(data.filter(n => !n.read).length);
    } catch (e) { /* ignore */ }
  }, [hotelId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (!hotelId) return;
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [hotelId, load]);

  const markRead = async (id) => {
    try {
      await notifApi.markRead(id);
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (e) { /* ignore */ }
  };

  const markAllRead = async () => {
    const unread = notifications.filter(n => !n.read);
    for (const n of unread) {
      await notifApi.markRead(n.id).catch(() => {});
    }
    setNotifications(prev => prev.map(n => ({ ...n, read: true })));
    setUnreadCount(0);
  };

  const formatTime = (ts) => {
    if (!ts) return '';
    const d = new Date(ts);
    const now = new Date();
    const diff = (now - d) / 1000;
    if (diff < 60) return 'Just now';
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return d.toLocaleDateString();
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
          <Bell className="w-5 h-5" />
          {unreadCount > 0 && (
            <span className="absolute -top-0.5 -right-0.5 w-4 h-4 bg-red-500 text-white text-[10px] rounded-full flex items-center justify-center font-bold">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80 p-0" align="end">
        <div className="flex items-center justify-between px-4 py-3 border-b border-border">
          <h3 className="font-semibold text-sm heading-font">Notifications</h3>
          {unreadCount > 0 && (
            <Button variant="ghost" size="sm" className="text-xs h-6" onClick={markAllRead} data-testid="mark-all-read">
              Mark all read
            </Button>
          )}
        </div>
        <ScrollArea className="max-h-[350px]">
          {notifications.length === 0 ? (
            <div className="p-6 text-center">
              <Bell className="w-8 h-8 text-muted-foreground mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">No notifications yet</p>
            </div>
          ) : (
            <div className="divide-y divide-border">
              {notifications.map(n => {
                const Icon = TYPE_ICONS[n.type] || Bell;
                const color = TYPE_COLORS[n.type] || 'text-muted-foreground';
                return (
                  <button key={n.id}
                          onClick={() => !n.read && markRead(n.id)}
                          className={`w-full text-left px-4 py-3 hover:bg-muted/50 transition-colors flex gap-3 ${!n.read ? 'bg-primary/5' : ''}`}
                          data-testid="notification-item">
                    <div className={`w-8 h-8 rounded-full bg-muted flex items-center justify-center flex-shrink-0 ${!n.read ? 'ring-2 ring-primary/20' : ''}`}>
                      <Icon className={`w-4 h-4 ${color}`} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm line-clamp-2 ${!n.read ? 'font-medium' : 'text-muted-foreground'}`}>
                        {n.message}
                      </p>
                      <p className="text-[10px] text-muted-foreground mt-0.5 flex items-center gap-1">
                        <Clock className="w-2.5 h-2.5" /> {formatTime(n.created_at)}
                      </p>
                    </div>
                    {!n.read && <div className="w-2 h-2 rounded-full bg-primary flex-shrink-0 mt-2" />}
                  </button>
                );
              })}
            </div>
          )}
        </ScrollArea>
      </PopoverContent>
    </Popover>
  );
}
